import { environment } from '../../../environments/environment';

/**
 * Who the funnel is allowed to send a customer back to, and how it remembers
 * where they came in from.
 *
 * The funnel is its own origin (`varaus.imt.fi`) and is linked into from a
 * growing set of sibling sites — `www.imt.fi`, campaign subdomains like
 * `ruotsiottelu.imt.fi`, one-off landing pages. "Back" has to mean the page they
 * actually left, so both the explicit `?returnTo=` link param
 * (`features/editor/entry-params.ts`) and the referrer fallback below resolve
 * through the one host check here.
 */

/** Where the entry referrer is remembered for the rest of the tab's session. */
const ENTRY_REFERRER_KEY = 'imtFunnelEntryReferrer';

/**
 * Is this host one we are willing to put behind a "back" link?
 *
 * The subdomain match is on a DOT BOUNDARY, not a bare suffix: `imt.fi` must not
 * match `evilimt.fi`. That single character is the whole control — the value
 * this gates ends up in an `<a href>` under our own domain, so a host that
 * slips through makes `varaus.imt.fi` a phishing-grade redirector.
 */
export function isAllowedReturnHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return environment.returnDomains.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

/**
 * Parse an absolute URL that is safe to hand a customer back to, or null.
 *
 * Beyond the host allowlist:
 * - `https:` only. `http:` would be a downgrade, and the scheme check is also
 *   what rejects `javascript:` and `data:` — neither of which has a host, so the
 *   allowlist alone would not stop them.
 * - No embedded credentials. `https://www.imt.fi@evil.example` parses with
 *   `hostname === 'evil.example'` so the allowlist already rejects it, but the
 *   reverse (`https://evil.example@www.imt.fi`) passes while reading, to a
 *   human, as the attacker's domain. Neither shape is ever a real back-link.
 */
export function parseAllowedReturnUrl(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;
  if (url.username !== '' || url.password !== '') return null;
  return isAllowedReturnHost(url.hostname) ? url.href : null;
}

/**
 * Is this referrer worth remembering as where the customer came in from, and if
 * so in what form? Null means "leave whatever is already stored alone".
 *
 * Split out from {@link captureEntryReferrer} so the decision is testable
 * without stubbing `document.referrer` and `window.location` — the same shape as
 * `shouldReloadAfterChunkFailure` in `core/monitoring/chunk-load-recovery.ts`.
 */
export function selectEntryReferrer(referrer: string, funnelHostname: string): string | null {
  // Absent (bookmark, typed URL, `rel=noreferrer`) — not a signal that they came
  // from nowhere, just that we were not told.
  if (!referrer) return null;

  const url = parseAllowedReturnUrl(referrer);
  if (url === null) return null;

  // The funnel linking to itself is not an exit. Without this, any full page
  // load from inside the funnel would overwrite a real campaign origin with our
  // own, and the back button would point at the page it is already on.
  return new URL(url).hostname === funnelHostname.toLowerCase() ? null : url;
}

/**
 * Remember the page the customer entered the funnel from, for links that carry
 * no `?returnTo=`.
 *
 * Campaign sites are built and edited by people who are not us, on a schedule
 * that is not ours, so "add a param to every Varaa link" cannot be the only way
 * the back button works. `document.referrer` gets that for free. Under the
 * default `strict-origin-when-cross-origin` policy a sibling subdomain sends
 * only its origin, so this typically lands on the campaign's front page rather
 * than the exact page — right site, right enough page, and strictly better than
 * dumping everyone on `www.imt.fi`.
 *
 * CALLED ONCE AT BOOTSTRAP, before the router runs, and stashed — not read lazily
 * at render time. `document.referrer` describes the document, so it survives the
 * landing → `/editor` navigation, but it does NOT survive the return trip from
 * Paytrail: after that hop the referrer is the payment provider, and a customer
 * who walks back to the editor would have lost their origin. Writing only when
 * the referrer is one we accept means that hop leaves the stored value alone.
 *
 * A referrer is a hint, never an instruction: it loses to an explicit
 * `?returnTo=` in `resolveBackUrl`.
 */
export function captureEntryReferrer(): void {
  const referrer = selectEntryReferrer(document.referrer, window.location.hostname);
  if (referrer === null) return;

  try {
    window.sessionStorage.setItem(ENTRY_REFERRER_KEY, referrer);
  } catch {
    // Blocked storage (private mode, cookies off). The back button falls back to
    // the default target — a worse link, not a broken one.
  }
}

/**
 * The remembered entry referrer, re-validated on the way out.
 *
 * Re-validating is not paranoia about the writer — it is the same origin's own
 * code. It is that `sessionStorage` outlives a deploy: a tab holding a value
 * written before {@link environment.returnDomains} was narrowed would otherwise
 * keep linking somewhere we have since stopped trusting.
 */
export function readEntryReferrer(): string | null {
  let stored: string | null;
  try {
    stored = window.sessionStorage.getItem(ENTRY_REFERRER_KEY);
  } catch {
    return null;
  }
  return stored === null ? null : parseAllowedReturnUrl(stored);
}
